export type BallLevel = 'red' | 'orange' | 'green' | 'yellow' | 'mixed';

export type SessionTemplate = {
  id: string;
  name: string;
  ballLevel: BallLevel;
  sessionType: 'group' | 'private' | 'clinic' | 'squads' | 'match_play';
  durationMinutes: number;
  ageRange: string;
  description: string;
  suggestedActivities: string[];
};

export const SESSION_TEMPLATES: SessionTemplate[] = [
  {
    id: 'red-ball-intro',
    name: 'Red Ball Intro',
    ballLevel: 'red',
    sessionType: 'group',
    durationMinutes: 45,
    ageRange: 'Ages 5–8',
    description: 'Fun, movement-based session introducing the basics with red foam balls on a short court.',
    suggestedActivities: [
      'Warm-up: caterpillar run and jumping jacks (5 min)',
      'Hand-eye coordination: catch and throw with foam ball, then drop-and-hit (10 min)',
      'Rally game: pairs try to reach 5 consecutive hits over the net (15 min)',
      'Mini match: first to 7 points, rotate partners (10 min)',
      'Cool-down: stretching circle, each player names one thing they did well (5 min)',
    ],
  },
  {
    id: 'orange-ball-development',
    name: 'Orange Ball Development',
    ballLevel: 'orange',
    sessionType: 'group',
    durationMinutes: 60,
    ageRange: 'Ages 8–10',
    description: 'Building consistency and basic court awareness on a three-quarter court with orange balls.',
    suggestedActivities: [
      'Warm-up: dynamic movement — side shuffles, high knees, spider runs (8 min)',
      'Groundstroke consistency: crosscourt forehand and backhand rallies, target cones (15 min)',
      'Serve introduction: continental grip, trophy position, simplified service motion (12 min)',
      'Tactics game: "keep it deep" — point scored only if ball lands past service line (15 min)',
      'Cool-down: short-court touch game, discuss one tactic used in the session (10 min)',
    ],
  },
  {
    id: 'green-ball-tactics',
    name: 'Green Ball Tactics',
    ballLevel: 'green',
    sessionType: 'group',
    durationMinutes: 60,
    ageRange: 'Ages 9–11',
    description: 'Developing tactical awareness, court positioning, and serve consistency on a full-length court.',
    suggestedActivities: [
      'Warm-up: live ball crosscourt warmup, coach feeds 2 balls per player (10 min)',
      'Pattern drill: rally to open court — hit behind, then down the line (15 min)',
      'Serve and return: serve from the T, receiver aims for deep return (12 min)',
      'Point play: first strike tennis — coach feeds short ball, player must attack (15 min)',
      'Cool-down: conditioned match play with one rule (e.g. first shot must be crosscourt) (8 min)',
    ],
  },
  {
    id: 'yellow-ball-performance',
    name: 'Yellow Ball Performance',
    ballLevel: 'yellow',
    sessionType: 'squads',
    durationMinutes: 90,
    ageRange: 'Ages 11+',
    description: 'Competitive match-play situations and performance under pressure on a full court.',
    suggestedActivities: [
      'Warm-up: extended live ball, coach rotates players through crosscourt and down-the-line patterns (15 min)',
      'Serve and return game: points played from deuce and ad positions, focus on placement (20 min)',
      'Pressure drill: 3 balls to stay in, one error and you rotate — builds mental resilience (20 min)',
      'Competitive sets: round-robin mini sets (first to 4 games), coach scores and tracks (25 min)',
      'Cool-down: match debrief — each player identifies one strength and one focus area (10 min)',
    ],
  },
  {
    id: 'mixed-levels-clinic',
    name: 'Mixed Levels Clinic',
    ballLevel: 'mixed',
    sessionType: 'clinic',
    durationMinutes: 60,
    ageRange: 'All ages',
    description: 'Station-based session accommodating multiple ability levels simultaneously.',
    suggestedActivities: [
      'Warm-up: all players together — ball handling circuits, juggling, bouncing (8 min)',
      'Station 1 — Red/Orange: cooperative rally target game with coach (15 min)',
      'Station 2 — Green/Yellow: competitive crosscourt drill with point scoring (15 min)',
      'Rotate stations (swap Red/Orange ↔ Green/Yellow groups)',
      'Cool-down: all players together — quick fun tournament game, everyone participates (7 min)',
    ],
  },
];

export const BALL_LEVEL_COLOURS: Record<BallLevel, { bg: string; text: string; dot: string }> = {
  red:    { bg: 'bg-red-50 dark:bg-red-950/40',       text: 'text-red-700 dark:text-red-400',       dot: 'bg-red-500' },
  orange: { bg: 'bg-orange-50 dark:bg-orange-950/40', text: 'text-orange-700 dark:text-orange-400', dot: 'bg-orange-500' },
  green:  { bg: 'bg-green-50 dark:bg-green-950/40',   text: 'text-green-700 dark:text-green-400',   dot: 'bg-green-500' },
  yellow: { bg: 'bg-yellow-50 dark:bg-yellow-950/40', text: 'text-yellow-700 dark:text-yellow-400', dot: 'bg-yellow-400' },
  mixed:  { bg: 'bg-slate-50 dark:bg-slate-800/40',   text: 'text-slate-700 dark:text-slate-300',   dot: 'bg-slate-400' },
};
